import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
  CURRENCIES,
  DATE_FORMATS,
  LANGUAGES,
  LOCALE_DEFAULTS,
  TIMEZONES,
  COUNTRIES,
  normalizeCountryCode,
  type LocaleConfigResponse,
  type LocaleSettings,
} from '../i18n/localeConfig';
import { translate } from '../i18n/translations';
import { formatMoney, formatWithDatePattern, getIntlLocale } from '../utils/localeFormat';
import { localeAPI } from '../services/api';
import { useAuth } from './AuthContext';

interface LocaleContextType {
  language: string;
  currency: string;
  timezone: string;
  dateFormat: string;
  country: string;
  localeReady: boolean;
  setLanguage: (lang: string) => void;
  setCurrency: (code: string) => void;
  setTimezone: (tz: string) => void;
  setDateFormat: (fmt: string) => void;
  setCountry: (code: string) => void;
  applyLocale: (settings: Partial<LocaleSettings>) => void;
  t: (key: string) => string;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  currencies: typeof CURRENCIES;
  timezones: typeof TIMEZONES;
  languages: typeof LANGUAGES;
  dateFormats: typeof DATE_FORMATS;
  countries: typeof COUNTRIES;
}

const STORAGE_KEY = 'tripiigo_locale';

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const loadStored = (): Partial<LocaleSettings> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const LocaleAuthSync: React.FC<{
  onLocale: (settings: LocaleSettings) => void;
}> = ({ onLocale }) => {
  const { user, authReady } = useAuth();

  useEffect(() => {
    if (!authReady) return;

    localeAPI.getMyLocale()
      .then((res) => {
        const data = res.data;
        onLocale({
          language: data.language || LOCALE_DEFAULTS.language,
          currency: data.currency || LOCALE_DEFAULTS.currency,
          timezone: data.timezone || LOCALE_DEFAULTS.timezone,
          dateFormat: data.dateFormat || LOCALE_DEFAULTS.dateFormat,
          country: normalizeCountryCode(data.country || LOCALE_DEFAULTS.country),
        });
      })
      .catch(() => {
        // keep current locale from config / storage
      });
  }, [user?.id, user?.tenantId, user?.company?.language, user?.company?.currency, user?.company?.timeZone, authReady, onLocale]);

  return null;
};

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const stored = loadStored();
  const [language, setLanguageState] = useState(stored.language || LOCALE_DEFAULTS.language);
  const [currency, setCurrencyState] = useState(stored.currency || LOCALE_DEFAULTS.currency);
  const [timezone, setTimezoneState] = useState(stored.timezone || LOCALE_DEFAULTS.timezone);
  const [dateFormat, setDateFormatState] = useState(stored.dateFormat || LOCALE_DEFAULTS.dateFormat);
  const [country, setCountryState] = useState(stored.country || LOCALE_DEFAULTS.country);
  const [localeReady, setLocaleReady] = useState(false);
  const [config, setConfig] = useState<LocaleConfigResponse | null>(null);

  const persist = useCallback((patch: Record<string, string>) => {
    const next = { ...loadStored(), ...patch };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const applyLocale = useCallback((settings: Partial<LocaleSettings>) => {
    if (settings.language) {
      setLanguageState(settings.language);
      persist({ language: settings.language });
    }
    if (settings.currency) {
      setCurrencyState(settings.currency);
      persist({ currency: settings.currency });
    }
    if (settings.timezone) {
      setTimezoneState(settings.timezone);
      persist({ timezone: settings.timezone });
    }
    if (settings.dateFormat) {
      setDateFormatState(settings.dateFormat);
      persist({ dateFormat: settings.dateFormat });
    }
    if (settings.country) {
      const country = normalizeCountryCode(settings.country);
      setCountryState(country);
      persist({ country });
    }
  }, [persist]);

  useEffect(() => {
    localeAPI.getConfig()
      .then((res) => {
        setConfig(res.data);
        // Only seed defaults when nothing is stored — tenant locale comes from /locale/me
        const existing = loadStored();
        if (!existing.language && !existing.currency) {
          applyLocale({
            ...res.data.defaults,
            country: normalizeCountryCode(res.data.defaults?.country),
          });
        }
      })
      .catch(() => {
        const existing = loadStored();
        if (!existing.language && !existing.currency) {
          applyLocale(LOCALE_DEFAULTS);
        }
      })
      .finally(() => setLocaleReady(true));
  }, [applyLocale]);

  const setLanguage = (lang: string) => { setLanguageState(lang); persist({ language: lang }); };
  const setCurrency = (code: string) => { setCurrencyState(code); persist({ currency: code }); };
  const setTimezone = (tz: string) => { setTimezoneState(tz); persist({ timezone: tz }); };
  const setDateFormat = (fmt: string) => { setDateFormatState(fmt); persist({ dateFormat: fmt }); };
  const setCountry = (code: string) => {
    const normalized = normalizeCountryCode(code);
    setCountryState(normalized);
    persist({ country: normalized });
  };

  const t = useCallback((key: string) => translate(language, key), [language]);

  const formatCurrency = useCallback((amount: number) => {
    return formatMoney(amount, currency, language, { maximumFractionDigits: 2 });
  }, [currency, language]);

  const formatDate = useCallback((date: Date | string, options?: Intl.DateTimeFormatOptions) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (
      options?.hour ||
      options?.minute ||
      options?.second ||
      options?.dateStyle ||
      options?.timeStyle
    ) {
      const locale = getIntlLocale(language, currency);
      return new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        ...options,
      }).format(d);
    }
    return formatWithDatePattern(d, dateFormat, language, timezone, options);
  }, [language, currency, timezone, dateFormat]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const languages = config?.languages || LANGUAGES;
  const currencies = config?.currencies || CURRENCIES;
  const timezones = config?.timezones || TIMEZONES;
  const dateFormats = config?.dateFormats || DATE_FORMATS;
  const countries = config?.countries || COUNTRIES;

  const value = useMemo(() => ({
    language, currency, timezone, dateFormat, country, localeReady,
    setLanguage, setCurrency, setTimezone, setDateFormat, setCountry, applyLocale,
    t, formatCurrency, formatDate,
    currencies, timezones, languages, dateFormats, countries,
  }), [
    language, currency, timezone, dateFormat, country, localeReady,
    setLanguage, setCurrency, setTimezone, setDateFormat, setCountry, applyLocale,
    t, formatCurrency, formatDate,
    currencies, timezones, languages, dateFormats, countries,
  ]);

  return (
    <LocaleContext.Provider value={value}>
      <LocaleAuthSync onLocale={applyLocale} />
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale requires LocaleProvider');
  return ctx;
};
